import { useEffect, useState } from "react";
import { Box, Button, LinearProgress, Typography } from "@mui/material";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import CheckIcon from "@mui/icons-material/Check";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DownloadIcon from "@mui/icons-material/FileDownloadOutlined";
import { useTranslation } from "react-i18next";
import CustomModal from "../../../components/modal/CustomModal";
import { common, financedEmissions } from "../../../i18n";
import { GeneratedReportFile, ReportFormat } from "../../../models/dto/cat15/cat15.types";
import { generateReport } from "../../../service/cat15.service";
import { downloadFromUrl } from "../../../service/report.service";

interface Props {
  open: boolean;
  onClose: () => void;
  year: number;
  formats: ReportFormat[]; // e.g. ["excel", "pdf"]
  sectors?: string[]; // level-1 section codes to scope the report
}

type Status = "generating" | "done" | "error";

const formatSize = (bytes: number): string => {
  if (bytes >= 1_000_000) return `${(bytes / 1_000_000).toFixed(1)} MB`;
  if (bytes >= 1_000) return `${Math.round(bytes / 1_000)} KB`;
  return `${bytes} B`;
};

const ProgressModal = ({ open, onClose, year, formats, sectors }: Props) => {
  const { t } = useTranslation([financedEmissions, common], {
    nsMode: "fallback",
  });
  const [progress, setProgress] = useState(0);
  const [status, setStatus] = useState<Status>("generating");
  const [files, setFiles] = useState<GeneratedReportFile[]>([]);

  // Download-button label — "Excel · XLSX · 2.1 MB" (Figma node 6170:7448).
  const fileLabel = (f: GeneratedReportFile): string => {
    const name = f.format === "excel" ? t("File-Excel") : t("File-PDF");
    const ext = f.format === "excel" ? "XLSX" : "PDF";
    return `${name} · ${ext} · ${formatSize(f.size)}`;
  };

  const stages = [
    t("Stage-Collecting"),
    t("Stage-Calculating"),
    t("Stage-Aggregating"),
    t("Stage-Building"),
  ];

  useEffect(() => {
    if (!open) {
      setProgress(0);
      setStatus("generating");
      setFiles([]);
      return;
    }

    let active = true;
    setStatus("generating");
    setProgress(0);
    setFiles([]);

    // Visual pacing: creep toward 90% while the request is in flight; the real
    // completion (or failure) drives the jump to 100% / error state.
    const timer = setInterval(() => {
      setProgress((p) => (p >= 90 ? 90 : p + 10));
    }, 250);

    generateReport(year, formats, "isic", sectors)
      .then((generated) => {
        if (!active) return;
        setFiles(generated);
        setProgress(100);
        setStatus("done");
      })
      .catch(() => {
        if (!active) return;
        setStatus("error"); // handleApiError already surfaced the alert
      })
      .finally(() => clearInterval(timer));

    return () => {
      active = false;
      clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, year]);

  const done = status === "done";
  const errored = status === "error";

  return (
    <CustomModal
      open={open}
      onClose={onClose}
      maxWidth="560px"
      showCloseIcon={done || errored}
      header={
        <Typography variant="h1">
          {t("Generating-Report")} — {year}
        </Typography>
      }
      body={
        <Box display="flex" flexDirection="column" gap="20px">
          {errored ? (
            <Box display="flex" alignItems="center" gap="8px">
              <ErrorOutlineIcon sx={{ color: "var(--red)", fontSize: 22 }} />
              <Typography variant="body1" color="var(--red)">
                {t("Report-Generate-Failed")}
              </Typography>
            </Box>
          ) : (
            <>
              {/* Green progress bar — Figma track #a2d6d0, fill --green, 8/16. */}
              <Box>
                <LinearProgress
                  variant="determinate"
                  value={progress}
                  sx={{
                    height: 8,
                    borderRadius: "16px",
                    backgroundColor: "var(--progress-track)",
                    "& .MuiLinearProgress-bar": {
                      backgroundColor: "var(--green)",
                      borderRadius: "16px",
                    },
                  }}
                />
                <Typography
                  variant="body2"
                  color="var(--gray9)"
                  mt="6px"
                  textAlign="right"
                >
                  {progress}%
                </Typography>
              </Box>

              {/* Checklist — 24px rounded-square ticks (Figma "tick-square"). */}
              <Box display="flex" flexDirection="column" gap="12px">
                {stages.map((stage, i) => {
                  const reached = progress >= (i + 1) * 25;
                  return (
                    <Box key={stage} display="flex" alignItems="center" gap="8px">
                      {reached ? (
                        <CheckBoxIcon sx={{ color: "var(--green)", fontSize: 24 }} />
                      ) : (
                        <CheckBoxOutlineBlankIcon
                          sx={{ color: "var(--gray5)", fontSize: 24 }}
                        />
                      )}
                      <Typography
                        variant="body1"
                        color={reached ? "var(--dark)" : "var(--gray1)"}
                      >
                        {stage}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}

          {done && (
            <Box
              display="flex"
              flexDirection="column"
              gap="10px"
              pt="8px"
              borderTop="1px solid var(--gray6)"
            >
              <Box display="flex" alignItems="center" gap="6px">
                <CheckIcon sx={{ color: "var(--green)", fontSize: 18 }} />
                <Typography fontSize="14px" lineHeight="21px" color="var(--green)">
                  {t("Report-Ready", { year })}
                </Typography>
              </Box>
              <Typography variant="body2" color="var(--gray9)">
                {t("Download-Files")}
              </Typography>
              <Box display="flex" gap="16px">
                {files.map((f) => (
                  <Button
                    key={f.format}
                    variant="outlined"
                    color="green"
                    startIcon={<DownloadIcon />}
                    onClick={() => downloadFromUrl(f.url, f.fileName)}
                    sx={{ flex: 1 }}
                  >
                    {fileLabel(f)}
                  </Button>
                ))}
              </Box>
            </Box>
          )}
        </Box>
      }
    />
  );
};

export default ProgressModal;
